import React, { useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import cl from "classnames";
import { getTimeOption, getTimesAfter } from "./helpers";
import EndTimeInput from "./EndTimeInput";
import { combineDateAndHour } from "venuescanner-js/utils";

const TimeInput = ({
  startDate,
  endDate,
  startTime,
  endTime,
  onChange,
  endError,
  className,
  defaultStartTime = "08:00",
  extraStartDateProps = {},
  extraEndDateProps = {},
  Component = "select",
}) => {
  const isToday = !!startDate?.isSame(moment(), "day");
  const minDate =
    !startDate || isToday ? moment() : combineDateAndHour(startDate, "00:00");
  const disabled = !startDate && !endDate;
  const times = disabled
    ? [defaultStartTime]
    : getTimesAfter(minDate, endDate || minDate);

  useEffect(() => {
    if (times.length > 0 && !times.includes(startTime)) {
      // If the time selected is invalid, default to defaultStartTime, or the first available time
      onChange({
        start: times[Math.max(times.indexOf(defaultStartTime), 0)],
        end: endTime,
      });
    }
  }, [times, startTime, endTime, onChange, defaultStartTime]);

  return (
    <>
      <label htmlFor="enq-time-from" className={cl("ux-select", className)}>
        <Component
          id="enq-time-from"
          value={startTime || ""}
          onChange={useCallback(
            ({ target }) => {
              const idx = times.indexOf(target.value) + 1;
              const endTimeIsValid =
                endTime === times[idx] ||
                (endTime &&
                  combineDateAndHour(startDate, target.value).isBefore(
                    combineDateAndHour(endDate, endTime)
                  ));
              onChange({
                start: target.value,
                end: endTimeIsValid ? endTime : times[idx],
              });
            },
            [onChange, startDate, endDate, endTime, times]
          )}
          disabled={disabled}
          {...extraStartDateProps}
        >
          {times.map(getTimeOption)}
        </Component>
      </label>

      <EndTimeInput
        hasError={!!endError}
        value={endTime}
        onChange={useCallback(
          (value) => onChange({ start: startTime, end: value }),
          [onChange, startTime]
        )}
        {...{
          startDate,
          startTime,
          endDate,
          className,
          disabled,
        }}
        Component={Component}
        {...extraEndDateProps}
      />
    </>
  );
};

TimeInput.propTypes = {
  startDate: PropTypes.instanceOf(moment),
  endDate: PropTypes.instanceOf(moment),
  startTime: PropTypes.string,
  endTime: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  endError: PropTypes.string,
  className: PropTypes.string,
  defaultStartTime: PropTypes.string,
  extraStartDateProps: PropTypes.object,
  extraEndDateProps: PropTypes.object,
  Component: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
};

export default TimeInput;
